/** /academy/plans — « Choose my plan » (D-110): the Academy Pass, the payment methods of the
 * learner's country, promo code or referral discount, EU withdrawal waiver, then the provider's
 * secure page (or our status page for a push to the phone and for the local simulator). */

import {
  ApiError,
  type AcademyCheckoutFallback,
  type SalesMethod,
  getAcademyAccountRetrieveQueryKey,
  useAcademyAccountRetrieve,
  useAcademyCheckoutCreate,
  useAcademyCheckoutNotifyRestoredCreate,
  useAcademyCheckoutQuoteCreate,
} from "@agencyos/api-client";
import {
  asFx,
  convertFromXaf,
  countryName,
  formatMoney,
  formatXaf,
  isEuConsumer,
  localText,
  phoneFor,
  withPaymentFee,
} from "@agencyos/core";
import { Badge, Button, Checkbox, GlassCard, Skeleton, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CreditCard, Lock, Mail, Smartphone, Tag, Wallet } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { fieldErrors } from "../../lib/settings";
import { PageHead } from "../ui";
import { PassCard } from "./PassCard";
import { TrialCard } from "./TrialCard";

function MethodIcon({ method }: { method: SalesMethod }) {
  if (method.provider === "moneroo") return <Wallet aria-hidden size={18} />; // Mobile Money or card (D-128)
  return method.flow === "redirect" && method.provider === "stripe_checkout" ? (
    <CreditCard aria-hidden size={18} />
  ) : (
    <Smartphone aria-hidden size={18} />
  );
}

/** Our own pages open in the app; the provider's page replaces the tab. */
export function goTo(url: string, navigate: (path: string) => void): void {
  try {
    const target = new URL(url, window.location.origin);
    // our own pages (status page, simulator) open in the app, even when the API names another
    // host for the web app (local development); providers' pages never live under /academy/
    if (target.origin === window.location.origin || target.pathname.startsWith("/academy/")) {
      navigate(`${target.pathname}${target.search}`);
      return;
    }
  } catch {
    /* not a URL: ignored below */
  }
  window.location.assign(url);
}

export function ChoosePlanPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const account = useAcademyAccountRetrieve({ query: { retry: false } });
  const data = account.data?.status === 200 ? account.data.data : undefined;
  const offer = data?.offer;
  const methods = offer?.methods ?? [];
  const [method, setMethod] = useState("");
  // D-142: the plans sold (30-day Academy Pass, 7-day Pass), prices from the catalogue
  const plans = offer?.plans ?? [];
  const [plan, setPlan] = useState(params.get("plan") ?? "");
  const chosenPlan = plans.find((p) => p.code === plan) ?? plans[0];
  const packageCode = chosenPlan?.code ?? "";
  const [phone, setPhone] = useState("");
  const [promo, setPromo] = useState("");
  const [waiver, setWaiver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const quote = useAcademyCheckoutQuoteCreate();
  const checkout = useAcademyCheckoutCreate();
  const notify = useAcademyCheckoutNotifyRestoredCreate();
  // D-132: the usual provider failed; its fallback offered with its total (D-133: order chosen)
  const [offered, setOffered] = useState<AcademyCheckoutFallback | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const checkoutError = checkout.error instanceof ApiError && [409, 503].includes(checkout.error.status) ? null : checkout.error;
  const errors = { ...fieldErrors(quote.error), ...fieldErrors(checkoutError) };
  const legendId = useId();
  const country = data?.country || "CM";
  const chosen = methods.find((m) => m.key === method) ?? methods[0];
  const priced = quote.data?.status === 200 ? quote.data.data : undefined;
  const amount = priced?.amount_xaf ?? chosenPlan?.price_xaf ?? offer?.price_xaf ?? 0;
  // D-131: the provider's fee paid by the learner (data; none in the EU/EEA), shown before payment
  const fee = chosen?.fee;
  const feeLabel = fee ? localText(fee.label as Record<string, string>, language) : "";
  const charged = withPaymentFee(amount, fee);
  const eu = isEuConsumer(country);
  const renewal = params.get("renew") === "1" || Boolean(data?.active_until);
  const anyTest = methods.some((m) => m.simulated);
  const maintenance = offer?.maintenance;
  const fallbackName = offered?.method.provider_name || maintenance?.fallback || "";
  const usualOffered = methods.some((m) => !m.fallback);
  const noMethodLeft = Boolean(maintenance?.notify) || unavailable;

  useEffect(() => {
    if (offer?.available && !quote.isPending) quote.mutate({ data: packageCode ? { package: packageCode } : {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- when the offer is known and when the plan changes
  }, [offer?.available, packageCode]);

  const applyPromo = () => promo.trim() && quote.mutate({ data: { promo_code: promo.trim(), ...(packageCode ? { package: packageCode } : {}) } });

  const onPay = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!chosen) return;
    if (eu && !waiver) return setError(t("academySales.plans.withdrawalRequired"));
    setUnavailable(false);
    if (chosen.needs_phone && phone && !phoneFor(phone, country)) return setError(t("academySales.signup.phoneInvalid"));
    checkout.mutate(
      {
        data: {
          method: chosen.key,
          phone: phone.trim(),
          promo_code: priced?.reason === "promo" ? promo.trim() : "",
          channel: "web",
          renewal,
          withdrawal_waiver: waiver,
          ...(packageCode ? { package: packageCode } : {}),
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAccountRetrieveQueryKey() });
          goTo(response.data.redirect_url, navigate);
        },
        onError: (failure) => {
          if (failure instanceof ApiError && failure.status === 409) {
            // D-132: the fallback has another fee: chosen for the learner, its total shown first
            const body = failure.body as AcademyCheckoutFallback;
            setOffered(body);
            setMethod(body.method.key);
            void queryClient.invalidateQueries({ queryKey: getAcademyAccountRetrieveQueryKey() });
            return;
          }
          if (failure instanceof ApiError && failure.status === 503) return setUnavailable(true);
          if (!(failure instanceof ApiError && failure.status === 400)) setError(t("common.error"));
        },
      },
    );
  };

  return (
    <div className="ac-page as-plans">
      <PageHead
        eyebrow={t("academySales.pass.eyebrow")}
        title={t("academySales.plans.title")}
        subtitle={<p>{t("academySales.plans.intro")}</p>}
      />
      {account.isLoading ? <Skeleton style={{ height: 320 }} /> : null}
      {account.isError ? <p className="ko">{t("common.error")}</p> : null}
      {offer && !offer.available ? <p className="ac-secondary">{t("academySales.pass.unavailable")}</p> : null}
      {offer?.available && data ? (
        <div className="as-plans__grid">
          <div className="as-plans__offer">
            <PassCard offer={offer} country={country} />
            <TrialCard offer={offer} trial={data.trial} />
          </div>
          <GlassCard as="section" level="thick" className="as-checkout" aria-labelledby="as-checkout-title">
            <form className="ac-form" onSubmit={onPay} noValidate>
              <h2 id="as-checkout-title" className="ac-title-2">
                {data.active_until ? t("academySales.pass.renew") : t("academySales.pass.choose")}
              </h2>
              {data.active_until ? <p className="ac-footnote">{t("academySales.plans.renewHint")}</p> : null}
              {plans.length > 1 ? (
                <fieldset className="as-methods as-plan-choice">
                  <legend className="as-methods__legend">{t("coaching.pass7.planLegend")}</legend>
                  {plans.map((p) => (
                    <label key={p.code} className={`as-method${chosenPlan?.code === p.code ? " as-method--on" : ""}`}>
                      <input type="radio" name="plan" value={p.code} checked={chosenPlan?.code === p.code} onChange={() => setPlan(p.code)} />
                      <span className="as-method__text">
                        <strong>{localText(p.name as Record<string, string>, language) || p.code}</strong>
                        <span className="ac-footnote ao-num">
                          {formatXaf(p.price_xaf)} · {p.monthly ? t("academySales.pass.perMonth") : t("coaching.pass7.forDays", { days: p.days })}
                        </span>
                        {p.short ? <span className="ac-footnote">{t("coaching.pass7.never")}</span> : null}
                      </span>
                      {p.short ? <Badge tone="offer">{t("coaching.pass7.badge")}</Badge> : null}
                    </label>
                  ))}
                </fieldset>
              ) : null}
              <fieldset className="as-methods" aria-describedby={legendId}>
                <legend className="as-methods__legend">{t("academySales.plans.method")}</legend>
                <p id={legendId} className="ac-footnote">
                  {t("academySales.plans.methodsFor", { country: countryName(country, language) })}{" "}
                  <Link className="ac-link" to="/academy/account#profile">
                    {t("academySales.plans.changeCountry")}
                  </Link>
                </p>
                {fallbackName && !noMethodLeft ? (
                  <p role="status" className="notice-warning">
                    {usualOffered && !offered
                      ? t("academySales.plans.fallbackDegraded", { provider: fallbackName })
                      : t("academySales.plans.fallbackNotice", { provider: fallbackName })}
                  </p>
                ) : null}
                {noMethodLeft ? (
                  <div role="alert" className="notice-warning as-unavailable">
                    <strong>
                      <CircleAlert aria-hidden size={16} /> {t("academySales.plans.unavailableTitle")}
                    </strong>
                    <span>{t("academySales.plans.unavailable")}</span>
                    {notify.isSuccess && notify.data.status === 201 ? (
                      <span role="status">{t("academySales.plans.notifyDone", { email: notify.data.data.email })}</span>
                    ) : (
                      <Button variant="secondary" disabled={notify.isPending} onClick={() => notify.mutate({ data: {} })}>
                        <Mail aria-hidden size={16} /> {t("academySales.plans.notifyMe")}
                      </Button>
                    )}
                  </div>
                ) : null}
                {methods.length === 0 && !noMethodLeft ? (
                  <p role="note" className="notice-warning">
                    {t("academySales.plans.noMethod")}
                  </p>
                ) : (
                  methods.map((m) => (
                    <label key={m.key} className={`as-method${chosen?.key === m.key ? " as-method--on" : ""}`}>
                      <input
                        type="radio"
                        name="method"
                        value={m.key}
                        checked={chosen?.key === m.key}
                        onChange={() => setMethod(m.key)}
                      />
                      <span className="as-method__icon">
                        <MethodIcon method={m} />
                      </span>
                      <span className="as-method__text">
                        <strong>{localText(m.label as Record<string, string>, language)}</strong>
                        <span className="ac-footnote">{localText(m.hint as Record<string, string>, language)}</span>
                        {m.operators.length ? (
                          <span className="ac-footnote">{t("academySales.plans.operators", { list: m.operators.join(", ") })}</span>
                        ) : null}
                      </span>
                      {m.fallback ? <Badge tone="offer">{t("academySales.plans.fallbackBadge")}</Badge> : null}
                      {m.simulated ? <Badge tone="warning">TEST</Badge> : null}
                    </label>
                  ))
                )}
              </fieldset>
              {errors.method ? <p className="ko">{errors.method}</p> : null}
              {chosen?.needs_phone ? (
                <TextField
                  label={t("academySales.plans.phone")}
                  hint={t("academySales.plans.phoneHint")}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  error={errors.phone}
                />
              ) : null}
              {offer.promo_enabled ? (
                <div className="as-promo">
                  <TextField
                    label={t("academySales.plans.promo")}
                    value={promo}
                    onChange={(event) => setPromo(event.target.value.toUpperCase())}
                    error={errors.promo_code}
                    autoComplete="off"
                  />
                  <Button variant="secondary" onClick={applyPromo} disabled={!promo.trim() || quote.isPending}>
                    <Tag aria-hidden size={16} /> {t("academySales.plans.promoApply")}
                  </Button>
                </div>
              ) : null}
              <dl className="as-total" aria-live="polite">
                {priced && priced.discount_xaf > 0 ? (
                  <>
                    <div>
                      <dt>{t("academySales.plans.listPrice")}</dt>
                      <dd className="ao-num">{formatXaf(priced.list_price_xaf)}</dd>
                    </div>
                    <div className="as-total__discount">
                      <dt>
                        {priced.reason === "referral"
                          ? t("academySales.plans.referralApplied", { amount: formatXaf(priced.discount_xaf) })
                          : t("academySales.plans.promoApplied", { amount: formatXaf(priced.discount_xaf) })}
                      </dt>
                      <dd className="ao-num">−{formatXaf(priced.discount_xaf)}</dd>
                    </div>
                  </>
                ) : null}
                {feeLabel ? (
                  <>
                    <div>
                      <dt>{t("academySales.plans.passPrice")}</dt>
                      <dd className="ao-num">{formatXaf(amount)}</dd>
                    </div>
                    <div className="as-total__fee">
                      <dt>{feeLabel}</dt>
                      <dd className="ao-num">{formatXaf(charged.fee)}</dd>
                    </div>
                  </>
                ) : null}
                <div className="as-total__sum">
                  <dt>{feeLabel ? t("academySales.plans.totalTtc") : t("academySales.plans.total")}</dt>
                  <dd className="ao-num">{formatXaf(charged.total)}</dd>
                </div>
                {fee?.exempt && feeLabel ? (
                  <p className="ac-footnote">
                    {fee.reason === "eu_eea" ? t("academySales.plans.feeNoneEu") : t("academySales.plans.feeNone")}
                  </p>
                ) : null}
                {chosen && chosen.currency !== "XAF" && chosen.currency !== "XOF" ? (
                  <p className="ac-footnote">
                    {t("academySales.plans.charged", {
                      amount: formatMoney(
                        convertFromXaf(charged.total, chosen.currency, asFx(offer.fx)) ?? 0,
                        chosen.currency,
                        language === "en" ? "en-GB" : "fr-FR",
                      ),
                    })}
                  </p>
                ) : null}
              </dl>
              {eu ? (
                <Checkbox
                  label={t("academySales.plans.withdrawal")}
                  checked={waiver}
                  onChange={(event) => setWaiver(event.target.checked)}
                />
              ) : null}
              {offered && chosen?.key === offered.method.key ? (
                <p role="status" className="ac-footnote">
                  {t("academySales.plans.fallbackTotal", { provider: offered.method.provider_name, total: formatXaf(charged.total) })}
                </p>
              ) : null}
              {error ? (
                <p className="ko" role="alert">
                  {error}
                </p>
              ) : null}
              {checkoutError instanceof ApiError && checkoutError.detail && !Object.keys(errors).length ? (
                <p className="ko" role="alert">
                  {checkoutError.detail}
                </p>
              ) : null}
              <Button type="submit" block disabled={!chosen || checkout.isPending}>
                <Lock aria-hidden size={16} />{" "}
                {checkout.isPending ? t("academySales.plans.paying") : t("academySales.plans.pay", { amount: formatXaf(charged.total) })}
              </Button>
              <p className="ac-footnote as-secure">
                {chosen?.provider_name
                  ? t("academySales.plans.secureWith", { provider: chosen.provider_name })
                  : t("academySales.plans.secure")}
              </p>
              {anyTest ? <p className="ac-footnote as-test">{t("academySales.plans.test")}</p> : null}
              <p className="ac-footnote">
                <Trans
                  i18nKey="academySales.plans.legal"
                  components={{
                    terms: <Link className="ac-link" to="/academy/legal/terms" />,
                    refund: <Link className="ac-link" to="/academy/legal/refund" />,
                  }}
                />
              </p>
            </form>
          </GlassCard>
        </div>
      ) : null}
    </div>
  );
}
